// src/features/people/components/ToolsSection.jsx - CON RESTRICCIONES PARA PERSONAS DADAS DE BAJA
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import { FaPlus, FaMinus, FaCubes, FaTags } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Componentes
import TableWrapper from '../../../components/TableWrapper';
import FilterSelect from '../../../components/FilterSelect';
import AssignEquipmentsModal from './AssignEquipmentsModal';
import UnassignEquipmentsModal from './UnassignEquipmentsModal';
import ToolProfileModal from '../../tools/components/ToolProfileModal';
import ToolsReport from './ToolsReport';

// Hooks
import { useColumnFilter } from '../../../hooks/useColumnFilter';

// Constantes y utilidades
import { TIPOS_EQUIPOS, getTipoDescripcion } from '../../../utils/constants';

// ✅ AGREGADA PROP isPersonDeactivated
const ToolsSection = ({ personId, tools = [], onDataChange, showSnackbar, isPersonDeactivated = false }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showToolProfileModal, setShowToolProfileModal] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState(null);
  const [filteredTools, setFilteredTools] = useState(tools);
  const navigate = useNavigate();

  const typeValueGetter = useCallback((tool) => {
    return getTipoDescripcion(tool.type, TIPOS_EQUIPOS);
  }, []);

  const { 
    filterOptions: nameOptions, 
    activeFilters: activeNames,
    handleOptionsUpdate: handleNameOptionsUpdate,
    handleFiltersChange: handleNameChange,
    filterData: filterByName 
  } = useColumnFilter(tools, 'name', null, `person-${personId}-tools`);

  const { 
    filterOptions: typeOptions, 
    activeFilters: activeTypes,
    handleOptionsUpdate: handleTypeOptionsUpdate,
    handleFiltersChange: handleTypeChange,
    filterData: filterByType 
  } = useColumnFilter(tools, 'type', typeValueGetter, `person-${personId}-tools`);

  useEffect(() => {
    let result = [...tools];
    
    if (activeNames && activeNames.length > 0) {
      result = filterByName(result);
    }
    
    if (activeTypes && activeTypes.length > 0) {
      result = filterByType(result);
    }
    
    setFilteredTools(result);
  }, [tools, activeNames, activeTypes, filterByName, filterByType]);

  const handleViewTool = useCallback((tool) => {
    setSelectedToolId(tool.id);
    setShowToolProfileModal(true);
  }, []);

  const toolColumns = useMemo(() => [
    { 
      headerName: 'Tipo', 
      field: 'type', 
      flex: 1,
      valueFormatter: params => getTipoDescripcion(params.value, TIPOS_EQUIPOS),
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Nombre', 
      field: 'name', 
      flex: 1.5, 
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Serial', 
      field: 'serial', 
      flex: 1, 
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Cantidad', 
      field: 'quantity', 
      width: 100, 
      filter: 'agNumberColumnFilter',
      floatingFilter: true
    }
  ], []);

  // ✅ VALIDACIÓN AGREGADA
  const handleOpenAssignModal = () => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden asignar equipos a una persona dada de baja');
      return;
    }
    setShowAssignModal(true);
  };

  // ✅ VALIDACIÓN AGREGADA
  const handleOpenUnassignModal = () => {
    if (isPersonDeactivated) {
      showSnackbar('warning', 'No se pueden desasignar equipos de una persona dada de baja');
      return;
    }
    setShowUnassignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
  };

  const handleCloseUnassignModal = () => {
    setShowUnassignModal(false);
  };

  const handleCloseToolProfileModal = () => {
    setShowToolProfileModal(false);
    setSelectedToolId(null);
  };

  const handleEquipmentsAssigned = () => {
    if (showSnackbar) {
      showSnackbar('success', 'Equipos asignados correctamente');
    }
    
    if (onDataChange) {
      onDataChange();
    }
  };

  const handleEquipmentsUpdate = () => {
    if (onDataChange) {
      onDataChange();
    }
  };

  const handleToolProfileUpdate = () => {
    if (onDataChange) {
      onDataChange();
    }
    
    if (showSnackbar) {
      showSnackbar('success', 'Herramienta actualizada correctamente');
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <FilterSelect
                options={nameOptions}
                activeFilters={activeNames}
                onFiltersChange={handleNameChange}
                onOptionsUpdate={handleNameOptionsUpdate}
                title="Filtrar por nombre"
                filterKey={`person-${personId}-tools-names`}
                icon={<FaCubes />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={typeOptions}
                activeFilters={activeTypes}
                onFiltersChange={handleTypeChange}
                onOptionsUpdate={handleTypeOptionsUpdate}
                title="Filtrar por tipo"
                filterKey={`person-${personId}-tools-types`}
                icon={<FaTags />}
                variant="outline-secondary"
              />
            </div>
            
            {/* ✅ BOTONES DESHABILITADOS SI ESTÁ DADA DE BAJA */}
            <div className="d-flex gap-2">
              <ToolsReport
                personId={personId}
                tools={tools}
                showSnackbar={showSnackbar}
              />
              
              <Button 
                variant="danger" 
                size="sm"
                className="d-flex align-items-center"
                onClick={handleOpenUnassignModal}
                disabled={tools.length === 0 || isPersonDeactivated}
              >
                <FaMinus className="me-1" /> Desasignar Equipos
              </Button>
              <Button 
                size="sm"
                className="d-flex align-items-center"
                onClick={handleOpenAssignModal}
                disabled={isPersonDeactivated}
              >
                <FaPlus className="me-1" /> Asignar Equipos
              </Button>
            </div>
          </div>
        </Col>
      </Row>
      
      <div className="flex-grow-1" style={{ height: 'calc(100vh - 280px)' }}>
        <TableWrapper 
          columnDefs={toolColumns}
          rowData={filteredTools}
          tableType="equipos"
          onView={handleViewTool}
          customGridOptions={{
            pagination: true,
            paginationPageSize: 50,
            domLayout: 'autoHeight'
          }}
        />
      </div>
      
      <AssignEquipmentsModal 
        show={showAssignModal}
        onHide={handleCloseAssignModal}
        personId={personId}
        onEquipmentsAssigned={handleEquipmentsAssigned}
      />
      
      <UnassignEquipmentsModal 
        show={showUnassignModal}
        onHide={handleCloseUnassignModal}
        personId={personId}
        assignedEquipments={tools}
        onEquipmentsUpdate={handleEquipmentsUpdate}
      />

      <ToolProfileModal
        show={showToolProfileModal}
        onHide={handleCloseToolProfileModal}
        toolId={selectedToolId}
        onToolUpdate={handleToolProfileUpdate}
        showSnackbar={showSnackbar}
        contextModule="people"
      />
    </div>
  );
};

export default ToolsSection;